import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { updatePageMeta, addStructuredData, addBreadcrumbSchema } from '../utils/seo';
import { metaFor, locationSchema, breadcrumbsFor } from '../seo/routeMeta';
import { getLocation } from '../data/locations';

// /locations/:city — local landing page for a service-area city.
// Data-driven from ../data/locations (add a city there, no code change here).
function LocationPage() {
  const { city } = useParams();
  const location = getLocation(city);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!location) return;

    const path = `/locations/${location.slug}`;
    // Same title, canonical, Service block and breadcrumb the server prints
    // into the raw HTML (seo/routeMeta, 2026-10-17).
    updatePageMeta(metaFor(path));
    addStructuredData(locationSchema(location));
    addBreadcrumbSchema(breadcrumbsFor(path));
  }, [location]);

  if (!location) {
    return (
      <div style={{ paddingTop: '140px', minHeight: '60vh', textAlign: 'center', padding: '140px 1.5rem 2rem' }}>
        <h1 style={{ fontFamily: "'Playfair Display', serif" }}>Area Not Found</h1>
        <p style={{ color: '#6b7280', marginBottom: '1rem' }}>We couldn't find that service area.</p>
        <Link to="/contact" style={{ color: '#2563eb' }}>Contact us about your area →</Link>
      </div>
    );
  }

  const { name, county, tagline, intro, angle, areas, nearby } = location;
  const nearbyPages = (nearby || []).map(getLocation).filter(Boolean);

  return (
    <div style={{ paddingTop: '140px', minHeight: '60vh' }}>
      {/* Hero */}
      <section style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', color: 'white', padding: '60px 0', textAlign: 'center' }}>
        <div className="container">
          <p style={{ textTransform: 'uppercase', letterSpacing: '0.15em', fontSize: '0.8rem', opacity: 0.7, marginBottom: '0.75rem' }}>
            Serving {county}
          </p>
          <h1 style={{ fontSize: '2.5rem', fontFamily: "'Playfair Display', serif", marginBottom: '0.5rem' }}>
            Real Estate Photography in {name}
          </h1>
          <p style={{ fontSize: '1.2rem', opacity: 0.85 }}>{tagline}</p>
        </div>
      </section>

      {/* Body */}
      <section style={{ padding: '60px 0' }}>
        <div className="container" style={{ maxWidth: '800px', margin: '0 auto', padding: '0 1.5rem' }}>
          {intro.map((para, i) => (
            <p key={i} style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#374151', marginBottom: '1.5rem' }}>{para}</p>
          ))}

          {/* Why local media matters here */}
          <div style={{ background: '#f9fafb', borderRadius: '12px', padding: '24px', margin: '1rem 0 2.5rem', borderLeft: '4px solid #c9a96e' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '1rem', color: '#6b7280' }}>Why it matters in {name}</h3>
            <p style={{ margin: 0, color: '#374151', lineHeight: '1.7' }}>{angle}</p>
          </div>

          {/* Areas served */}
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', fontFamily: "'Playfair Display', serif" }}>Areas we cover in {name}</h2>
          <ul style={{ listStyle: 'none', padding: 0, marginBottom: '2.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '6px' }}>
            {areas.map((a, i) => (
              <li key={i} style={{ padding: '8px 0', display: 'flex', alignItems: 'center', gap: '10px', color: '#374151' }}>
                <span style={{ color: '#c9a96e', fontWeight: 'bold' }}>•</span>{a}
              </li>
            ))}
          </ul>

          {/* Services */}
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', fontFamily: "'Playfair Display', serif" }}>What we shoot</h2>
          <p style={{ fontSize: '1.05rem', lineHeight: '1.8', color: '#374151', marginBottom: '1rem' }}>
            Full listing media for {name} properties — HDR interior &amp; exterior photography, aerial/drone, twilight, cinematic video tours, Matterport 3D tours, and virtual staging — plus land and vacant lot photography, priced by the acre.
          </p>
          <p style={{ marginBottom: '2.5rem' }}>
            <Link to="/services" style={{ color: '#2563eb', fontWeight: 600 }}>See all services &amp; packages →</Link>
            {' '}&nbsp;·&nbsp;{' '}
            <Link to="/services/land" style={{ color: '#2563eb', fontWeight: 600 }}>Land &amp; lots →</Link>
          </p>

          {/* Nearby cities (internal links) */}
          {nearbyPages.length > 0 && (
            <>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', fontFamily: "'Playfair Display', serif" }}>Nearby areas we serve</h2>
              <p style={{ marginBottom: '2.5rem', color: '#374151' }}>
                {nearbyPages.map((n, i) => (
                  <React.Fragment key={n.slug}>
                    <Link to={`/locations/${n.slug}`} style={{ color: '#2563eb', fontWeight: 600 }}>{n.name}</Link>
                    {i < nearbyPages.length - 1 ? '  ·  ' : ''}
                  </React.Fragment>
                ))}
              </p>
            </>
          )}

          {/* CTA */}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <Link to="/book" style={{ padding: '14px 32px', background: '#c9a96e', color: 'white', borderRadius: '6px', textDecoration: 'none', fontWeight: '600' }}>
              Book a {name} Shoot
            </Link>
            <Link to="/contact" style={{ padding: '14px 32px', border: '2px solid #1a1a2e', color: '#1a1a2e', borderRadius: '6px', textDecoration: 'none', fontWeight: '500' }}>
              Get a Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default LocationPage;
